/* ==========================================================================
   RAM Bikes — hero carousel + slide-1 video
   ==========================================================================

   WHAT THIS DOES
   Drives the homepage hero: four slides picked by the dots the Figma file
   draws, plus the video that opens slide 1.

   NOT IN THE FIGMA FILE. The file draws one hero and four dots, with no
   states, no transition and no second slide. Everything below is ours.

   IT DOES NOT ADVANCE ON ITS OWN. It used to rotate every 7s. That was
   removed on request when the video landed, and the removal is why a good
   deal of machinery is gone with it: no interval, no pause-on-hover, no
   pause-on-blur, no pause button, and no aria-live juggling. Slides change
   only when a reader asks for one.

   THE PATTERN
   WAI-ARIA APG "carousel with tabbed slide picker": the dot row is a
   `tablist`, each dot a `tab`, each slide a `tabpanel`. That gives screen
   reader users a familiar model — "tab 2 of 4, selected" — instead of an
   unlabelled set of buttons. Roving tabindex, so the row is ONE tab stop and
   the arrow keys move within it.

   Because nothing changes without a deliberate act, the slide container is
   `aria-live="polite"` permanently. The old off-while-rotating dance existed
   only to stop automatic changes talking over the reader.

   WCAG 2.2.2 (Pause, Stop, Hide)
   Satisfied by construction rather than by a control. 2.2.2 governs motion
   that starts automatically and runs for MORE than five seconds; the carousel
   is manual and the video plays once for 5.04s and stops. Set the video to
   loop and that stops being true — a pause mechanism has to come back with it.

   THE VIDEO
   No `src` in the markup: `media` on <source> inside <video> is ignored by
   browsers, so the responsive choice has to happen here. Not assigning a src
   until we have decided to play also means a reduced-motion visitor fetches
   nothing at all.

   MARKUP CONTRACT
     [data-hero-slideshow]   the section
     .hero__slide            one per slide, in order
     [role="tab"]            one per slide, in the same order
     [data-hero-video]       optional, inside slide 1; needs data-src-sm/lg
     [data-hero-prev/next]   optional, OUTSIDE .hero__slides (inert)
   Sets [data-hero-ready] on the section, which is what home.css keys the
   cross-fade off — see the note there.
   ========================================================================== */

(function () {
  'use strict';

  var root = document.querySelector('[data-hero-slideshow]');
  if (!root) return;

  var slides = Array.prototype.slice.call(root.querySelectorAll('.hero__slide'));
  var tabs = Array.prototype.slice.call(root.querySelectorAll('[role="tab"]'));
  var slidesBox = root.querySelector('.hero__slides');
  var video = root.querySelector('[data-hero-video]');

  /* ---- the video ---------------------------------------------------------
     Runs before the carousel guard below, because slide 1 carries the video
     whether or not there is more than one slide to page through. */

  function startVideo() {
    if (!video) return;

    // Asked not to be moved: show the last frame as a still and fetch no
    // video at all. `preload="none"` plus a src that is never assigned means
    // zero bytes, not a poster plus a couple of MB nobody will watch.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      goStatic();
      return;
    }

    // 48em is the breakpoint where home.css switches the hero to the portrait
    // crop, so it is also where the smaller encode stops being enough.
    var small = window.matchMedia('(max-width: 47.9375em)').matches;
    var src = video.getAttribute(small ? 'data-src-sm' : 'data-src-lg');
    if (!src) return;

    video.src = src;
    tryPlay();
  }

  /* Give up on the video and show the last frame instead. One-way: once the
     still is up, swapping back mid-visit would be its own little glitch. */
  function goStatic() {
    if (!video) return;
    video.parentNode.classList.add('is-static');
    video.removeAttribute('src');
    video.load();              // drop the partial fetch
  }

  /* NOT every rejected play() means autoplay was refused, and the difference
     matters because the responses are opposite.

       NotAllowedError  the browser will not autoplay here — a per-site
                        setting, or a policy that wants a gesture even for
                        muted video. Nothing we do changes it: go static.

       AbortError       transient. Chrome pauses muted, video-only media in a
                        BACKGROUND tab to save power and reports exactly this.
                        A page opened in a background tab — a middle-click, a
                        "open in new tab" — hits it on load through no fault
                        of the visitor. Treating it as a refusal, which an
                        earlier pass did, meant that visitor got the still
                        permanently and never saw the video even after
                        switching to the tab. So: leave everything set up and
                        let the visibilitychange handler start it on arrival.

     Measured, not assumed — this is the error the browser actually returned:
     "The play() request was interrupted because video-only background media
     was paused to save power." */
  function tryPlay() {
    if (!video || !video.getAttribute('src')) return;
    if (document.hidden) return;             // visibilitychange will pick it up

    var attempt = video.play();
    if (attempt && typeof attempt.catch === 'function') {
      attempt.catch(function (err) {
        if (err && err.name === 'NotAllowedError' && !document.hidden) goStatic();
      });
    }
  }

  /* A tab that is backgrounded mid-play would come back part-way through the
     reveal, which reads as a glitch rather than an intro. Pausing holds the
     frame; returning resumes from there. Nothing restarts, because a reveal
     that plays twice is worse than one that finishes late. */
  function bindVideoVisibility() {
    if (!video) return;
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) {
        if (!video.paused) video.pause();
      } else if (!video.duration || video.currentTime < video.duration) {
        tryPlay();
      }
    });
  }

  startVideo();
  bindVideoVisibility();

  /* ---- the carousel ------------------------------------------------------ */

  // Nothing to page through. Leave the markup exactly as the CSS default
  // renders it rather than half-initialising. The video above still runs.
  if (slides.length < 2 || tabs.length !== slides.length) return;

  var index = 0;

  /* Slides 2-4 ship `loading="lazy"` so they cost nothing at first paint —
     only slide 1 is on the LCP path. But a lazy image stacked at opacity 0
     never enters the viewport as far as the browser is concerned, so it is
     never fetched, and the slide arrives BLANK the first time. Measured:
     after init, slides 2-4 all reported complete=false, naturalWidth=0.

     So the carousel loads one slide ahead of itself. Flipping .loading to
     'eager' on an image that has not started yet is what kicks off the
     fetch. */
  function ensureLoaded(i) {
    var slide = slides[(i + slides.length) % slides.length];
    var img = slide && slide.querySelector('img');
    if (img && img.loading === 'lazy') img.loading = 'eager';
  }

  function render() {
    ensureLoaded(index);
    ensureLoaded(index + 1);

    slides.forEach(function (slide, i) {
      var on = i === index;
      if (on) slide.setAttribute('data-active', '');
      else slide.removeAttribute('data-active');

      // `inert` takes the whole subtree out of the focus order AND the
      // accessibility tree, which is what stops a screen reader from reading
      // all four headlines as one run-on block.
      if (on) slide.removeAttribute('inert');
      else slide.setAttribute('inert', '');
    });

    tabs.forEach(function (tab, i) {
      var on = i === index;
      tab.setAttribute('aria-selected', on ? 'true' : 'false');
      tab.tabIndex = on ? 0 : -1;
    });

    // Paging away from slide 1 hides the video; letting it play on behind a
    // transparent slide burns decode for nothing.
    if (video && video.getAttribute('src')) {
      if (index !== 0) {
        if (!video.paused) video.pause();
      } else if (!video.duration || video.currentTime < video.duration) {
        tryPlay();
      }
    }
  }

  function goTo(i, opts) {
    index = (i + slides.length) % slides.length;
    render();
    if (opts && opts.focus) tabs[index].focus();
  }

  tabs.forEach(function (tab, i) {
    tab.addEventListener('click', function () { goTo(i); });
  });

  /* Prev/next arrows — ajuste 2 del 1 sep 2026.

     They WRAP: from slide 4, next goes to 1. goTo() already takes the
     modulus, so this is the existing behaviour of the dots' arrow keys
     rather than a new rule, and it is why neither button is ever disabled.

     No { focus: true }. The arrows are their own tab stop; pulling focus
     onto the dot row on every click would move the keyboard user somewhere
     they did not ask to go, and pressing the same arrow twice would then
     stop working. */
  var prev = root.querySelector('[data-hero-prev]');
  var next = root.querySelector('[data-hero-next]');

  if (prev) prev.addEventListener('click', function () { goTo(index - 1); });
  if (next) next.addEventListener('click', function () { goTo(index + 1); });

  // Arrow keys move within the row; Home/End jump to the ends. Moving focus
  // also selects, which is the APG's "automatic activation" tablist — right
  // here because selecting a slide is instant and has no cost.
  root.querySelector('[role="tablist"]').addEventListener('keydown', function (event) {
    var delta = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[event.key];
    var target = null;

    if (delta) target = index + delta;
    else if (event.key === 'Home') target = 0;
    else if (event.key === 'End') target = slides.length - 1;
    else return;

    event.preventDefault();
    goTo(target, { focus: true });
  });

  root.setAttribute('data-hero-ready', '');
  if (slidesBox) slidesBox.setAttribute('aria-live', 'polite');
  render();

  // Exposed for the audit harness and for debugging.
  window.RAMHero = {
    get index() { return index; },
    get videoPlaying() { return !!(video && video.src && !video.paused); },
    goTo: goTo
  };
})();
